import { useCallback, useEffect, useState } from "react";
import Model from "@tripian/model";
import { api } from "@tripian/core";
import moment from "moment";

export interface UseTourApiParams {
  cityId?: string;
  providerId?: string;
  lat?: number;
  lng?: number;
  date?: string;
  to?: string;
  adults?: number;
  currency?: string;
  limit?: number;
  offset?: number;
  lang?: string;
  radius?: number;
  keywords?: string | undefined;
  categories?: string | undefined;
  sortingBy?: string | undefined;
  sortingType?: string | undefined;
}

const useTourApi = (params: UseTourApiParams) => {
  const {
    cityId,
    providerId,
    lat,
    lng,
    date,
    to,
    adults = 2,
    currency = "usd",
    limit = 10,
    offset = 0,
    lang = "en",
    radius = 10,
    keywords,
    categories,
    sortingBy,
    sortingType,
  } = params;

  const isActive =
    typeof window !== "undefined" &&
    window.tconfig &&
    window.tconfig.SHOW_TOURS_AND_TICKETS &&
    providerId !== undefined;

  const [tours, setTours] = useState<Model.TourSearchProduct[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(!!isActive);
  const [error, setError] = useState<string | undefined>();

  const fetchTours = useCallback(async () => {
    if (!isActive || !cityId || !providerId || lat === undefined || lng === undefined) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(undefined);

    const today = moment().startOf("day");
    const clampedDate = date && moment(date).isBefore(today)
      ? today.format("YYYY-MM-DD")
      : date || today.format("YYYY-MM-DD");

    const request: Partial<Model.TourSearchRequest> = {
      cityId,
      providerId,
      lat,
      lng,
      date: clampedDate,
      adults,
      currency,
      limit,
      offset,
      lang,
      radius,
      ...(to ? { to } : {}),
      ...(keywords ? { keywords } : {}),
      ...(categories ? { categories } : {}),
      ...(sortingBy ? { sortingBy } : {}),
      ...(sortingType ? { sortingType } : {}),
    } as Model.TourSearchRequest;

    const maxRetries = 1;
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await api.tourSearch(request as Model.TourSearchRequest);
        setTours(result.products || []);
        setTotal(result.total || 0);
        lastError = undefined;
        break;
      } catch (err: any) {
        lastError = err;
        if (attempt < maxRetries) {
          console.warn(`Tour Search failed, retrying (${attempt + 1}/${maxRetries})...`);
          await new Promise((r) => setTimeout(r, 2000));
          continue;
        }
        console.error("Tour Search Error:", err);
        setTours([]);
        setTotal(0);
        setError(err?.message || "Tour search failed");
      }
    }

    setLoading(false);
  }, [isActive, cityId, providerId, lat, lng, date, to, adults, currency, limit, offset, lang, radius, keywords, categories, sortingBy, sortingType]);

  useEffect(() => {
    if (isActive && cityId && providerId && lat !== undefined && lng !== undefined) {
      fetchTours();
    }
  }, [fetchTours]);

  return {
    tours,
    loading,
    error,
    total,
    fetchTours,
  };
};

export default useTourApi;
